import { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableRowReorderEvent } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";

import { CancellationReason } from "../../models/eventCancellation";
import {
  useCreateCancellationReasonMutation,
  useDeleteCancellationReasonMutation,
  useGetCancellationReasonsQuery,
  useReorderCancellationReasonsMutation,
  useResetCancellationReasonsMutation,
  useUpdateCancellationReasonMutation,
} from "../../store/services/event-cancellations";
import { useGetBoMeQuery } from "../../store/services/api";
import { useTenantScope } from "../../hooks/useTenantScope";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import { CATALOG_CONTENT_STYLE, CatalogCard, CatalogSearchField } from "../../components/ui/CatalogCard";
import { emptyMessage } from "../../components/ui/EmptyState";
import { FormField, FormRow, FormToggle } from "../../components/ui/FormField";

const REASON_TEXT_MAX_LENGTH = 100;
const USER_FACING_MESSAGE_MAX_LENGTH = 500;
const TABLE_ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

const normalizeSearchText = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const GLOBAL_REASON_IMPACT_MESSAGE =
  "Este motivo es global: lo comparten todos los tenants y el cambio impacta en todos.";

const GlobalReasonImpactMessage = () => (
  <Message
    className="w-full justify-content-start mb-3"
    severity="warn"
    text={GLOBAL_REASON_IMPACT_MESSAGE}
  />
);

interface ReasonFormValues {
  reason_text: string;
  user_facing_message: string;
  active: boolean;
}

const validationSchema = Yup.object().shape({
  reason_text: Yup.string()
    .trim()
    .max(REASON_TEXT_MAX_LENGTH, `Máximo ${REASON_TEXT_MAX_LENGTH} caracteres`)
    .required("El motivo es requerido"),
  user_facing_message: Yup.string().max(
    USER_FACING_MESSAGE_MAX_LENGTH,
    `Máximo ${USER_FACING_MESSAGE_MAX_LENGTH} caracteres`
  ),
});

export const CancellationReasonsSection = () => {
  const scope = useTenantScope();
  const { data: me } = useGetBoMeQuery();
  const isSuperAdmin = scope.role === "SUPER_ADMIN";
  const tenantId = scope.tenantId;
  const isGlobalCatalog = !scope.loading && isSuperAdmin && tenantId === null;
  const canManage = isSuperAdmin || !!tenantId;

  const { data: reasons, isLoading } = useGetCancellationReasonsQuery(
    { tenantId, includeInactive: true },
    { skip: scope.loading }
  );
  const [createReason, { isLoading: isCreating }] = useCreateCancellationReasonMutation();
  const [updateReason, { isLoading: isUpdating }] = useUpdateCancellationReasonMutation();
  const [deleteReason, { isLoading: isDeleting }] = useDeleteCancellationReasonMutation();
  const [reorderReasons] = useReorderCancellationReasonsMutation();
  const [resetReasons, { isLoading: isResetting }] = useResetCancellationReasonsMutation();

  const [orderedReasons, setOrderedReasons] = useState<CancellationReason[]>([]);
  const [nameSearch, setNameSearch] = useState("");
  const [tableFirst, setTableFirst] = useState(0);
  const [tableRows, setTableRows] = useState(10);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [reasonToEdit, setReasonToEdit] = useState<CancellationReason | undefined>();
  const [reasonToView, setReasonToView] = useState<CancellationReason | undefined>();
  const [reasonToDelete, setReasonToDelete] = useState<CancellationReason | undefined>();
  const [resetDialogVisible, setResetDialogVisible] = useState(false);
  const [togglingReasonId, setTogglingReasonId] = useState<number | null>(null);

  useEffect(() => {
    setOrderedReasons(reasons ?? []);
  }, [reasons]);

  const tenantLabels = useMemo(() => {
    const labels = new Map<string, string>();
    (me?.tenants ?? []).forEach((tenant) => labels.set(tenant.id, tenant.provider || tenant.id));
    return labels;
  }, [me?.tenants]);

  const hasOwnReasons = orderedReasons.some((reason) => reason.tenantId !== null);
  const canReorder = canManage && !nameSearch.trim();
  const filteredReasons = useMemo(() => {
    const term = normalizeSearchText(nameSearch);
    if (!term) return orderedReasons;
    return orderedReasons.filter((reason) =>
      normalizeSearchText(reason.reasonText).includes(term)
    );
  }, [orderedReasons, nameSearch]);

  const formik = useFormik<ReasonFormValues>({
    initialValues: {
      reason_text: reasonToEdit?.reasonText ?? "",
      user_facing_message: reasonToEdit?.userFacingMessage ?? "",
      active: reasonToEdit?.active ?? true,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      if (!tenantId && !isGlobalCatalog) {
        toast.error("Elegí un tenant antes de guardar");
        return;
      }

      try {
        if (reasonToEdit) {
          await updateReason({
            id: reasonToEdit.id,
            reason_text: values.reason_text.trim(),
            user_facing_message: values.user_facing_message.trim() || null,
            active: values.active,
          }).unwrap();
          toast.success("Motivo actualizado");
        } else {
          await createReason({
            reason_text: values.reason_text.trim(),
            user_facing_message: values.user_facing_message.trim() || null,
            tenant_id: tenantId ?? null,
            active: values.active,
          }).unwrap();
          toast.success("Motivo creado");
        }
        hideDialog();
      } catch (err: unknown) {
        toast.error(getApiErrorMessage(err) ?? "No se pudo guardar el motivo");
      }
    },
  });

  const hideDialog = () => {
    setDialogVisible(false);
    setReasonToEdit(undefined);
    formik.resetForm();
  };

  const handleRowReorder = async (e: DataTableRowReorderEvent<CancellationReason[]>) => {
    if (!canReorder) return;

    const sourceIndex = e.dragIndex + tableFirst;
    const targetIndex = e.dropIndex + tableFirst;
    const previous = orderedReasons;
    const next = [...orderedReasons];
    const [moved] = next.splice(sourceIndex, 1);
    if (!moved) return;
    next.splice(targetIndex, 0, moved);

    const changed = next
      .map((reason, index) => ({ id: reason.id, display_order: index + 1 }))
      .filter(({ id, display_order }) => {
        const current = orderedReasons.find((reason) => reason.id === id);
        return current !== undefined && current.displayOrder !== display_order;
      });

    if (!changed.length) return;

    setOrderedReasons(
      next.map((reason, index) => ({ ...reason, displayOrder: index + 1 }))
    );
    try {
      await reorderReasons(changed).unwrap();
      toast.success("Orden actualizado");
    } catch (err: unknown) {
      setOrderedReasons(previous);
      toast.error(getApiErrorMessage(err) ?? "No se pudo actualizar el orden");
    }
  };

  const handleToggleActive = async (reason: CancellationReason, nextActive: boolean) => {
    if (!canManage || togglingReasonId === reason.id || reason.active === nextActive) {
      return;
    }

    const previous = orderedReasons;
    setTogglingReasonId(reason.id);
    setOrderedReasons((current) =>
      current.map((row) => (row.id === reason.id ? { ...row, active: nextActive } : row))
    );

    try {
      await updateReason({ id: reason.id, active: nextActive }).unwrap();
      toast.success(nextActive ? "Motivo activado" : "Motivo desactivado");
    } catch (err: unknown) {
      setOrderedReasons(previous);
      toast.error(getApiErrorMessage(err) ?? "No se pudo actualizar el estado del motivo");
    } finally {
      setTogglingReasonId(null);
    }
  };

  const handleDelete = async () => {
    if (!reasonToDelete) return;
    try {
      await deleteReason(reasonToDelete.id).unwrap();
      toast.success(
        reasonToDelete.usageCount
          ? "Motivo desactivado; se mantiene en el historial"
          : "Motivo eliminado"
      );
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) ?? "No se pudo eliminar el motivo");
    } finally {
      setReasonToDelete(undefined);
    }
  };

  const handleReset = async () => {
    try {
      await resetReasons().unwrap();
      toast.success("Motivos reseteados");
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) ?? "No se pudo resetear los motivos");
    } finally {
      setResetDialogVisible(false);
    }
  };

  const scopeTemplate = (reason: CancellationReason) =>
    reason.tenantId ? tenantLabels.get(reason.tenantId) ?? reason.tenantId : "Global";

  const truncatedTextTemplate = (
    text: string | null | undefined,
    maxWidth: string
  ) => {
    const value = text?.trim();
    if (!value) return <span className="text-500">—</span>;
    return (
      <span
        className="cancellation-reason-truncated block overflow-hidden text-overflow-ellipsis white-space-nowrap"
        style={{ maxWidth }}
        data-pr-tooltip={value}
        data-pr-position="top"
      >
        {value}
      </span>
    );
  };

  const reasonTextTemplate = (reason: CancellationReason) =>
    truncatedTextTemplate(reason.reasonText, "16rem");

  const userFacingMessageTemplate = (reason: CancellationReason) =>
    truncatedTextTemplate(reason.userFacingMessage, "20rem");

  const statusTemplate = (reason: CancellationReason) => (
    <InputSwitch
      inputId={`cancellation-reason-active-${reason.id}`}
      aria-label={`Activo: ${reason.reasonText}`}
      checked={reason.active}
      disabled={!canManage || togglingReasonId === reason.id}
      onChange={(e) => handleToggleActive(reason, Boolean(e.value))}
    />
  );

  const actionsTemplate = (reason: CancellationReason) => (
    <div className="flex gap-2">
      <Button
        type="button"
        icon="pi pi-eye"
        rounded
        outlined
        aria-label="Ver detalle del motivo"
        tooltip="Ver detalle"
        tooltipOptions={{ position: "top" }}
        onClick={() => setReasonToView(reason)}
      />
      {canManage ? (
        <>
          <Button
            type="button"
            icon="pi pi-pencil"
            rounded
            outlined
            severity="info"
            aria-label="Editar motivo"
            tooltip="Editar"
            tooltipOptions={{ position: "top" }}
            onClick={() => {
              setReasonToEdit(reason);
              setDialogVisible(true);
            }}
          />
          <Button
            type="button"
            icon="pi pi-trash"
            rounded
            outlined
            severity="danger"
            aria-label="Eliminar motivo"
            tooltip="Eliminar"
            tooltipOptions={{ position: "top" }}
            onClick={() => setReasonToDelete(reason)}
          />
        </>
      ) : null}
    </div>
  );

  return (
    <CatalogCard
      heading="Motivos de cancelación"
      actions={
        <>
          <Button
            type="button"
            icon="pi pi-plus"
            rounded
            size="small"
            aria-label="Agregar motivo"
            tooltip="Agregar motivo"
            disabled={scope.loading || (!isGlobalCatalog && !tenantId)}
            onClick={() => {
              setReasonToEdit(undefined);
              setDialogVisible(true);
            }}
          />
          {!!tenantId && hasOwnReasons && (
            <Button
              type="button"
              icon="pi pi-replay"
              rounded
              outlined
              size="small"
              severity="secondary"
              aria-label="Restablecer motivos"
              tooltip="Restablecer motivos"
              disabled={scope.loading || isResetting}
              onClick={() => setResetDialogVisible(true)}
            />
          )}
        </>
      }
    >
      <div className="flex flex-wrap mb-3">
        <CatalogSearchField
          inputId="cancellationReasonSearch"
          value={nameSearch}
          onChange={(value) => {
            setNameSearch(value);
            setTableFirst(0);
          }}
        />
      </div>

      {isGlobalCatalog ? <GlobalReasonImpactMessage /> : null}

      <Tooltip
        target=".cancellation-reason-truncated"
        style={{ maxWidth: "24rem", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
      />

      <DataTable
        value={filteredReasons}
        loading={scope.loading || isLoading}
        dataKey="id"
        paginator
        rows={tableRows}
        first={tableFirst}
        rowsPerPageOptions={TABLE_ROWS_PER_PAGE_OPTIONS}
        onPage={(e) => {
          setTableFirst(e.first);
          setTableRows(e.rows);
        }}
        reorderableRows={canReorder}
        onRowReorder={handleRowReorder}
        emptyMessage={emptyMessage("motivos de cancelación", Boolean(nameSearch.trim()))}
      >
        {canReorder && <Column rowReorder style={{ width: "3rem" }} />}
        <Column field="displayOrder" header="Orden" style={{ width: "6rem" }} />
        <Column
          field="reasonText"
          header="Motivo"
          body={reasonTextTemplate}
          style={{ maxWidth: "16rem", overflow: "hidden" }}
        />
        <Column
          field="userFacingMessage"
          header="Mensaje para el usuario"
          body={userFacingMessageTemplate}
          style={{ maxWidth: "20rem", overflow: "hidden" }}
        />
        <Column header="Alcance" body={scopeTemplate} style={{ width: "12rem" }} />
        <Column header="Estado" body={statusTemplate} style={{ width: "8rem" }} />
        <Column header="Acciones" body={actionsTemplate} style={{ width: "12rem" }} />
      </DataTable>

      <Dialog
        visible={!!reasonToView}
        header="Detalle del motivo"
        modal
        style={{ width: "42rem", maxWidth: "95vw", maxHeight: "90vh" }}
        breakpoints={{ "960px": "95vw" }}
        contentStyle={{ ...CATALOG_CONTENT_STYLE, overflowY: "auto" }}
        onHide={() => setReasonToView(undefined)}
      >
        {reasonToView && (
          <div className="flex flex-column gap-4">
            <div className="flex flex-column gap-2">
              <h2
                className="text-xl md:text-2xl font-semibold text-900 m-0"
                style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
              >
                {reasonToView.reasonText}
              </h2>
              <div className="flex flex-wrap align-items-center gap-2">
                {reasonToView.active ? (
                  <Tag severity="success" value="Activo" />
                ) : (
                  <Tag severity="danger" value="Inactivo" />
                )}
                <Tag severity="secondary" value={scopeTemplate(reasonToView)} />
              </div>
            </div>

            <div className="grid m-0">
              <div className="col-12 md:col-6 p-1">
                <div className="surface-card border-1 surface-border border-round p-3 h-full">
                  <small className="text-600 block mb-1">Orden</small>
                  <span className="text-2xl font-semibold text-900">
                    {reasonToView.displayOrder}
                  </span>
                </div>
              </div>
              <div className="col-12 md:col-6 p-1">
                <div className="surface-card border-1 surface-border border-round p-3 h-full">
                  <small className="text-600 block mb-1">Alcance</small>
                  <span
                    className="text-2xl font-semibold text-900"
                    style={{ wordBreak: "break-word" }}
                  >
                    {scopeTemplate(reasonToView)}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <small className="text-600 block mb-2">Mensaje para el usuario</small>
              {reasonToView.userFacingMessage?.trim() ? (
                <div
                  className="p-3 border-1 surface-border border-round surface-card"
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    maxHeight: "22rem",
                    overflow: "auto",
                  }}
                >
                  {reasonToView.userFacingMessage}
                </div>
              ) : (
                <span className="text-500">—</span>
              )}
            </div>
          </div>
        )}
      </Dialog>

      <Dialog

        contentStyle={CATALOG_CONTENT_STYLE}
        header={reasonToEdit ? "Editar motivo" : "Agregar motivo"}
        visible={dialogVisible}
        style={{ width: "32rem", maxWidth: "95vw" }}
        onHide={hideDialog}
        footer={
          <div className="form-actions flex flex-wrap justify-content-end" style={{ gap: "var(--space-18)" }}>
            <Button
              label="Guardar cambios"
              loading={isCreating || isUpdating}
              onClick={() => formik.handleSubmit()}
            />
            <Button
              label="Cancelar"
              severity="secondary"
              outlined
              onClick={hideDialog}
            />
          </div>
        }
      >
        {isGlobalCatalog ? <GlobalReasonImpactMessage /> : null}

        <FormRow>
          <FormField
            label="Motivo (interno)"
            htmlFor="reason_text"
            required
            error={formik.touched.reason_text ? formik.errors.reason_text : undefined}
          >
            <InputText
              id="reason_text"
              className="w-full"
              invalid={Boolean(formik.touched.reason_text && formik.errors.reason_text)}
              maxLength={REASON_TEXT_MAX_LENGTH}
              value={formik.values.reason_text}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField
            label="Mensaje para el usuario (opcional)"
            htmlFor="user_facing_message"
            error={formik.touched.user_facing_message ? formik.errors.user_facing_message : undefined}
            hint="Se usa para la comunicación diferenciada al usuario."
          >
            <InputTextarea
              id="user_facing_message"
              className="w-full"
              invalid={Boolean(formik.touched.user_facing_message && formik.errors.user_facing_message)}
              rows={3}
              maxLength={USER_FACING_MESSAGE_MAX_LENGTH}
              value={formik.values.user_facing_message}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </FormField>
        </FormRow>
        <FormToggle label="Activo" htmlFor="active">
          <InputSwitch
            inputId="active"
            checked={formik.values.active}
            onChange={(e) => formik.setFieldValue("active", e.value)}
          />
        </FormToggle>
      </Dialog>

      <Dialog

        contentStyle={CATALOG_CONTENT_STYLE}
        header="Eliminar motivo"
        visible={!!reasonToDelete}
        style={{ width: "32rem", maxWidth: "95vw" }}
        onHide={() => setReasonToDelete(undefined)}
        footer={
          <div className="form-actions flex flex-wrap justify-content-end" style={{ gap: "var(--space-18)" }}>
            <Button
              label="Eliminar motivo"
              severity="danger"
              loading={isDeleting}
              onClick={handleDelete}
            />
            <Button
              label="Cancelar"
              severity="secondary"
              outlined
              onClick={() => setReasonToDelete(undefined)}
            />
          </div>
        }
      >
        {reasonToDelete && !reasonToDelete.tenantId ? (
          <GlobalReasonImpactMessage />
        ) : null}
        {reasonToDelete?.usageCount ? (
          <p className="m-0">
            Este motivo fue usado en {reasonToDelete.usageCount}{" "}
            {reasonToDelete.usageCount === 1 ? "cancelación" : "cancelaciones"}. Se
            desactivará y no aparecerá en el dropdown, pero se mantendrá en el historial.
          </p>
        ) : (
          <p className="m-0">
            ¿Eliminar este motivo? Si ya fue usado en alguna cancelación se desactiva en
            lugar de borrarse, para no romper el historial.
          </p>
        )}
      </Dialog>

      <Dialog

        contentStyle={CATALOG_CONTENT_STYLE}
        header="Restablecer motivos"
        visible={resetDialogVisible}
        style={{ width: "32rem", maxWidth: "95vw" }}
        onHide={() => setResetDialogVisible(false)}
        footer={
          <div className="form-actions flex flex-wrap justify-content-end" style={{ gap: "var(--space-18)" }}>
            <Button
              label="Restablecer motivos"
              severity="danger"
              loading={isResetting}
              onClick={handleReset}
            />
            <Button
              label="Cancelar"
              severity="secondary"
              outlined
              onClick={() => setResetDialogVisible(false)}
            />
          </div>
        }
      >
        <p className="m-0">
          Las copias de este tenant se dan de baja y volvés a usar los motivos
          globales. Las cancelaciones ya registradas no se tocan.
        </p>
      </Dialog>
    </CatalogCard>
  );
};
